import moment from 'moment-timezone';

import { Exception, ScheduleHours } from '../models';
import { Intervals } from '../types';
import { getIntervals } from './get-intervals';
import { getStartAndEndOfDay } from './get-start-and-end-of-the-day';
import { getExceptions } from './get-exceptions';

/**
 * Returns working hours for a date including next day
 * @param scheduleHours
 * @param exceptions
 * @param date
 * @param timezone
 * @param isPublic
 */
export function getWorkingHours(
  scheduleHours: ScheduleHours,
  exceptions: Exception[],
  date: number,
  timezone: string,
  isPublic = false,
): Intervals {
  const dateMoment = moment.tz(date, timezone);
  const day = dateMoment.format('ddd');

  const intervals = getIntervals(scheduleHours, exceptions, date, timezone, false, isPublic);
  const [startOfDay, endOfDay] = getStartAndEndOfDay(dateMoment);
  const hours: Intervals = [];
  const dateExceptions = getExceptions(exceptions, dateMoment.format('D-M-YYYY'), timezone);
  // TODO: ask Nikita why need variables below
  let datePublicDisabledException = null;
  let dateOpenedException = null;
  let dateClosedException = null;

  for (const exception of dateExceptions) {
    if (isPublic && exception.publicDisabled) {
      datePublicDisabledException = exception;
    }

    if (!exception.closed) {
      dateOpenedException = exception;
    }

    if (exception.closed) {
      dateClosedException = exception;
    }
  }

  if ((scheduleHours[day].closed || (isPublic && scheduleHours[day].publicDisabled)) && (dateOpenedException && !datePublicDisabledException)) {
    for (const interval of intervals) {
      if (interval[1] <= startOfDay || interval[0] >= endOfDay) {
        continue;
      }

      const [start, end] = interval;
      hours.push([Math.max(start, startOfDay), end]);
    }
  }

  const closed = scheduleHours[day].closed || !!dateClosedException;
  const publicDisabled = isPublic && (scheduleHours[day].publicDisabled || datePublicDisabledException);
  if (closed || publicDisabled) {
    const previousDateMoment = dateMoment.subtract(1, 'days');
    const previousDay = previousDateMoment.format('ddd');
    const previousDayPublicDisabled = isPublic && scheduleHours[previousDay].publicDisabled;
    const previousDayNexDayEnabled = scheduleHours[previousDay].nextDay && !previousDayPublicDisabled;
    const previousDayExceptions = getExceptions(exceptions, previousDateMoment.format('D-M-YYYY'), timezone);
    let previousDayPublicDisabledException = null;
    let previousDayExceptionWithNextDay = null;

    for (const exception of previousDayExceptions) {
      if (isPublic && exception.publicDisabled) {
        previousDayPublicDisabledException = exception;
      }

      if (!exception.closed && !previousDayPublicDisabledException && exception.nextDay) {
        previousDayExceptionWithNextDay = exception;
      }
    }

    if (previousDayExceptionWithNextDay || previousDayNexDayEnabled) {
      for (const interval of intervals) {
        if (interval[1] <= startOfDay || interval[0] >= endOfDay) {
          continue;
        }

        const [start, end] = interval;
        hours.push([Math.max(start, startOfDay), end]);
      }
    }

    return hours;
  }

  for (const interval of intervals) {
    if (interval[1] <= startOfDay || interval[0] >= endOfDay) {
      continue;
    }

    const [start, end] = interval;
    hours.push([Math.max(start, startOfDay), end]);
  }

  return hours;
}
